import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appRoutes } from '@/routes/routes';
import { mergeSearchParams, normalizeSearchString } from '@/utils/urlSearch';
import {
  buildDashboardOverviewFilterUpdates,
  getDashboardOverviewFiltersFromSearch,
} from '@/utils/dashboardOverviewUrlFilters';

const routes = appRoutes();

/** Routes that read/write shared report query params (`member`, `start`, `end`, `tz`, optional `team`). */
export const REPORT_FILTERS_PATHNAMES = Object.freeze(
  new Set([
    routes.dashboardOverview,
    routes.timeline,
    routes.appUsage,
    routes.screenshots,
    routes.teamsTimeline,
    routes.teamsReporting,
    routes.userAttendance,
  ])
);

/**
 * Applies URL query → local state when navigation brings a new query string.
 *
 * - `POP`: browser back/forward
 * - `PUSH`: in-app navigation (e.g. `<Link to="/dashboard-overview?...">`)
 *
 * Ignores `REPLACE` (from `useDashboardOverviewUrlWrite`) so our own URL sync
 * does not fight dropdown/date state.
 *
 * `membersOptionsRef` must point at the latest options list so the listener can
 * resolve `member` ids (listener closes over a ref to avoid stale arrays).
 *
 * Optional `teamOptionsRef` + `setSelectedTeam` apply the same pattern for `team`
 * on team-scoped routes (e.g. teams-reporting).
 */
export function useDashboardOverviewPopSync({
  history,
  setDateRange,
  setIgnoreUrlTimezone,
  setSelectedMember,
  membersOptionsRef,
  teamOptionsRef,
  setSelectedTeam,
  pathnames = REPORT_FILTERS_PATHNAMES,
}) {
  useEffect(() => {
    return history.listen((nextLocation, action) => {
      if (!pathnames.has(nextLocation.pathname)) return;
      if (action !== 'POP' && action !== 'PUSH') return;

      const parsed = getDashboardOverviewFiltersFromSearch(
        nextLocation.search || ''
      );

      if (parsed.start && parsed.end) {
        setDateRange((prev) => {
          if (prev.start === parsed.start && prev.end === parsed.end)
            return prev;
          return { start: parsed.start, end: parsed.end };
        });
      }

      if (parsed.member) {
        const opts = membersOptionsRef.current;
        if (opts?.length) {
          const fromUrl = opts.find((m) => m.value === parsed.member);
          if (fromUrl) {
            setSelectedMember((prev) => {
              if (prev?.value === fromUrl.value) return prev;
              return fromUrl;
            });
          }
        }
      }

      if (teamOptionsRef && setSelectedTeam && parsed.team) {
        const opts = teamOptionsRef.current;
        if (opts?.length) {
          const fromUrl = opts.find((t) => t.value === parsed.team);
          if (fromUrl) {
            setSelectedTeam((prev) => {
              if (prev?.value === fromUrl.value) return prev;
              return fromUrl;
            });
          }
        }
      }

      if (parsed.tz) {
        setIgnoreUrlTimezone(false);
      }
    });
  }, [
    history,
    setDateRange,
    setIgnoreUrlTimezone,
    setSelectedMember,
    membersOptionsRef,
    teamOptionsRef,
    setSelectedTeam,
    pathnames,
  ]);
}

/**
 * Writes dashboard filter params into `location.search` via `history.replace`,
 * preserving unrelated keys (`cid`, `subscription`, …). Skips when the normalized
 * query would not change — avoids replace loops with React Router.
 *
 * When `memberId` from state is still unset (e.g. full page refresh before member
 * options load), keeps the existing `member` query param instead of merging an empty
 * value that would erase deep links.
 */
export function useDashboardOverviewUrlWrite({
  teamId,
  memberId,
  teamEntityId,
  dateRange,
  selectedTimezone,
  status,
  history,
  location,
}) {
  useEffect(() => {
    if (!teamId || !dateRange?.start || !dateRange?.end) {
      return;
    }

    const parsedCurrent = getDashboardOverviewFiltersFromSearch(
      location.search || ''
    );
    const memberIdForMerge =
      memberId != null && memberId !== ''
        ? memberId
        : parsedCurrent.member || '';

    const updates = buildDashboardOverviewFilterUpdates({
      memberId: memberIdForMerge,
      teamId: teamEntityId ?? '',
      start: dateRange.start,
      end: dateRange.end,
      tz: selectedTimezone || '',
      status: status || '',
    });
    const nextSearch = mergeSearchParams(location.search, updates);

    if (
      normalizeSearchString(location.search) ===
      normalizeSearchString(nextSearch)
    ) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: nextSearch,
    });
  }, [
    teamId,
    memberId,
    teamEntityId,
    dateRange.start,
    dateRange.end,
    selectedTimezone,
    status,
    history,
    location.pathname,
    location.search,
  ]);
}

/**
 * Pop/push URL → state, default member from `member` param, and TZ override helpers.
 * Call {@link useDashboardOverviewUrlWrite} after `useMemberCompanyTimezones` so `selectedTimezone` exists.
 */
export function useReportFiltersPopAndMemberHydration({
  history,
  location,
  teamId,
  currentUserId,
  membersOptions,
  selectedMember,
  setSelectedMember,
  setDateRange,
  membersOptionsRef,
}) {
  const filtersFromUrl = useMemo(
    () => getDashboardOverviewFiltersFromSearch(location.search),
    [location.search]
  );
  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  useDashboardOverviewPopSync({
    history,
    setDateRange,
    setIgnoreUrlTimezone,
    setSelectedMember,
    membersOptionsRef,
  });

  useEffect(() => {
    if (!teamId) {
      setSelectedMember(null);
      return;
    }
    if (membersOptions.length === 0) return;
    if (selectedMember) return;

    const urlMemberId = filtersFromUrl.member;
    if (urlMemberId) {
      const fromUrl = membersOptions.find((m) => m.value === urlMemberId);
      if (fromUrl) {
        setSelectedMember(fromUrl);
        return;
      }
    }

    const defaultMember =
      membersOptions.find((m) => m.value === currentUserId) ||
      membersOptions[0];
    if (defaultMember) {
      setSelectedMember(defaultMember);
    }
  }, [
    teamId,
    membersOptions,
    currentUserId,
    selectedMember,
    filtersFromUrl.member,
    setSelectedMember,
  ]);

  const onMemberFilterChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMember(opt);
    },
    [setSelectedMember]
  );

  return {
    urlTimezoneForHook,
    userOverrideTimezoneRef,
    setIgnoreUrlTimezone,
    onMemberFilterChange,
  };
}
